import {
  FORMULES_CATALOGUE,
  type FormuleCatalogue,
  type FormuleId,
  type Periodicite,
} from "@/config/formules";

export type { FormuleId, Periodicite } from "@/config/formules";

export type AbonnementStatut = "actif" | "en_attente_paiement" | "brouillon" | "expire" | "resilie";

export interface LimitesFormule {
  proprietaires: number | null;
  immeubles: number | null;
  biens: number | null;
  locataires: number | null;
  contrats: number | null;
  employes: number | null;
}

export interface Formule {
  id: FormuleId;
  nom: string;
  tagline: string;
  prixMensuel: number;
  prixAnnuel: number;
  highlight: boolean;
  limites: LimitesFormule;
  avantages: string[];
}

/** Avantages spécifiques à l'espace Agence (multi-propriétaires, employés). */
const AGENCE_AVANTAGES: Record<FormuleId, string[]> = {
  gratuit: [
    "1 propriétaire",
    "1 immeuble",
    "5 biens",
    "5 locataires",
    "5 contrats",
    "1 employé",
    "Fonctionnalités essentielles",
  ],
  essentiel: [
    "Jusqu'à 5 propriétaires",
    "Jusqu'à 3 immeubles",
    "Jusqu'à 30 biens",
    "Jusqu'à 40 locataires et contrats",
    "Loyers, paiements et impayés",
    "2 employés",
  ],
  professionnel: [
    "Jusqu'à 20 propriétaires",
    "Jusqu'à 10 immeubles",
    "Jusqu'à 150 biens",
    "Locataires et contrats illimités",
    "Statistiques avancées",
    "5 employés",
  ],
  agence: [
    "Propriétaires et immeubles illimités",
    "Biens illimités",
    "Locataires et contrats illimités",
    "Employés et autorisations illimités",
    "Rapports avancés",
    "Support prioritaire",
  ],
};

/**
 * Formules de l'espace Agence (montants en FCFA).
 * Construites à partir des valeurs lues dans Supabase (source de vérité unique)
 * via `useFormules`. `null` signifie « illimité ».
 */
export function toEspaceFormules(catalogue: FormuleCatalogue[]): Formule[] {
  return catalogue.map((formule) => ({
    id: formule.id,
    nom: formule.nom,
    tagline: formule.tagline,
    prixMensuel: formule.prixMensuel,
    prixAnnuel: formule.prixAnnuel,
    highlight: formule.highlight,
    limites: {
      proprietaires: formule.limites.proprietaires,
      immeubles: formule.limites.immeubles,
      biens: formule.limites.biens,
      locataires: formule.limites.locataires,
      contrats: formule.limites.contrats,
      employes: formule.limites.employes,
    },
    avantages: AGENCE_AVANTAGES[formule.id],
  }));
}

export const ABONNEMENT_STATUT_LABELS: Record<AbonnementStatut, string> = {
  actif: "Actif",
  en_attente_paiement: "Paiement requis",
  brouillon: "Non activé",
  expire: "Expiré",
  resilie: "Résilié",
};

export const ABONNEMENT_STATUT_TONES: Record<AbonnementStatut, "success" | "neutral" | "accent" | "danger"> = {
  actif: "success",
  en_attente_paiement: "accent",
  brouillon: "neutral",
  expire: "accent",
  resilie: "danger",
};

export function getFormule(id: FormuleId, catalogue: Formule[] = []): Formule {
  // Le catalogue provient de Supabase (source de vérité). On garantit une liste
  // non vide pour ne jamais retourner `undefined` : si le catalogue n'est pas
  // encore chargé, on retombe sur la configuration locale validée.
  const liste = catalogue.length > 0 ? catalogue : toEspaceFormules(FORMULES_CATALOGUE);
  return liste.find((formule) => formule.id === id) ?? liste[0];
}

/**
 * Modèle de données prévu pour Supabase.
 * Table `abonnements` : id uuid, owner_id, formule_id text, periodicite text,
 * statut text, date_debut date, date_fin date, created_at timestamptz.
 * Les prix et les limites proviennent de `@/config/formules` (table `formules`).
 */